<template>
  <div class="ai-check-container">
    <!-- 1. HEADER BANNER (Premium Deep Blue Gradient) -->
    <div class="ai-header-banner">
      <div class="banner-overlay"></div>
      <div class="banner-content">
        <div class="banner-left">
          <div class="ai-icon-pulse">
            <i class="el-icon-data-analysis"></i>
          </div>
          <div>
            <h1 class="banner-title">QUẢN LÝ DỮ LIỆU HỆ THỐNG</h1>
            <p class="banner-subtitle">
              Trung tâm quản trị lưu trữ AWS S3, bảo trì dung lượng CSDL lớn, chuyển đổi dữ liệu V4 & kiểm tra dịch vụ tích hợp.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- MAIN TABS CARD (Glassmorphism + Standard Tabs) -->
    <div class="glass-card" v-loading="!actionStatusesReady">
      <el-tabs v-if="actionStatusesReady" v-model="activeTab" type="card" class="sk-tabs">
        <!-- ════════════════════════════════════════════════════════════ -->
        <!-- TAB 1: QUẢN TRỊ LƯU TRỮ & AWS S3 -->
        <!-- ════════════════════════════════════════════════════════════ -->
        <el-tab-pane v-if="hasS3Actions" name="s3_storage">
          <span slot="label">
            <i class="el-icon-upload"></i> Lưu trữ & AWS S3
          </span>

          <div class="tab-pane-content">
            <el-row :gutter="20">
              <!-- Section 1.1: Chuyển ảnh lên S3 -->
              <el-col v-if="isTransfer === 'Hoạt động'" :xs="24" :lg="12" class="mb-4">
                <div class="form-section">
                  <div class="section-header-row">
                    <h4 class="section-divider">
                      <i class="el-icon-upload"></i> ĐỒNG BỘ ẢNH TỪ MÁY CHỦ LÊN AWS S3
                    </h4>
                    <span class="status-badge" :class="isTransfer === 'Hoạt động' ? 'online' : 'offline'">
                      <span class="dot"></span> {{ isTransfer }}
                    </span>
                  </div>

                  <p class="section-desc">
                    Đồng bộ và đẩy tệp hình ảnh từ máy chủ cục bộ lên dịch vụ lưu trữ đám mây S3 Amazon.
                  </p>

                  <div v-if="transferDate" class="notice-box info mb-3">
                    <i class="el-icon-info"></i>
                    <span>Giới hạn an toàn: Chỉ được chuyển dữ liệu phát sinh trước ngày <strong>{{ transferDate }}</strong></span>
                  </div>

                  <div class="form-item-block mb-3">
                    <label class="item-label"><i class="el-icon-date"></i> Khoảng thời gian áp dụng:</label>
                    <el-date-picker
                      v-model="dateRangeS3"
                      type="daterange"
                      align="left"
                      unlink-panels
                      range-separator="đến"
                      start-placeholder="Từ ngày"
                      end-placeholder="Đến ngày"
                      format="dd/MM/yyyy"
                      value-format="yyyy-MM-dd"
                      :picker-options="pickerOptions"
                      style="width: 100%"
                      @change="handleDateRangeS3Change"
                    />
                  </div>

                  <div class="section-action-footer">
                    <el-button
                      class="gradient-btn green-btn"
                      icon="el-icon-upload2"
                      size="medium"
                      :disabled="isTransfer === 'Ngừng hoạt động' || showCheckBoxS3"
                      @click="transferDataServerToS3"
                    >
                      Chọn trường & Chuyển ảnh lên S3
                    </el-button>
                  </div>
                </div>
              </el-col>

              <!-- Section 1.2: Xóa album trên Server & S3 -->
              <el-col v-if="isDeleteAlbum === 'Hoạt động'" :xs="24" :lg="12" class="mb-4">
                <div class="form-section">
                  <div class="section-header-row">
                    <h4 class="section-divider">
                      <i class="el-icon-delete"></i> XÓA ALBUM TRÊN MÁY CHỦ & AWS S3
                    </h4>
                    <span class="status-badge" :class="isDeleteAlbum === 'Hoạt động' ? 'online' : 'offline'">
                      <span class="dot"></span> {{ isDeleteAlbum }}
                    </span>
                  </div>

                  <p class="section-desc">
                    Dọn dẹp và xóa hoàn toàn album ảnh trên cả hai máy chủ theo khoảng thời gian đã chọn.
                  </p>

                  <div v-if="transferDate" class="notice-box warning mb-3">
                    <i class="el-icon-warning-outline"></i>
                    <span>Ràng buộc: Không được xóa album sau ngày <strong>{{ transferDate }}</strong></span>
                  </div>

                  <div class="form-item-block mb-3">
                    <label class="item-label"><i class="el-icon-date"></i> Khoảng thời gian xóa album:</label>
                    <el-date-picker
                      v-model="dateRangeDeleteS3"
                      type="daterange"
                      align="left"
                      unlink-panels
                      range-separator="đến"
                      start-placeholder="Từ ngày"
                      end-placeholder="Đến ngày"
                      format="dd/MM/yyyy"
                      value-format="yyyy-MM-dd"
                      :picker-options="pickerOptions"
                      style="width: 100%"
                      @change="handleDateRangeDeleteS3Change"
                    />
                  </div>

                  <div class="section-action-footer">
                    <el-button
                      class="gradient-btn red-btn"
                      icon="el-icon-delete"
                      size="medium"
                      :disabled="isDeleteAlbum === 'Ngừng hoạt động' || showCheckBoxS3"
                      @click="deleteDateServerAndS3"
                    >
                      Chọn trường & Xóa album
                    </el-button>
                  </div>
                </div>
              </el-col>

              <!-- Section 1.3: Quét ảnh tồn tại S3 -->
              <el-col v-if="isScanPictureExitOnS3 === 'Hoạt động'" :xs="24" :lg="12" class="mb-4">
                <div class="form-section">
                  <div class="section-header-row">
                    <h4 class="section-divider">
                      <i class="el-icon-view"></i> QUÉT KIỂM TRA TÍNH TOÀN VẸN ẢNH S3
                    </h4>
                    <span class="status-badge" :class="isScanPictureExitOnS3 === 'Hoạt động' ? 'online' : 'offline'">
                      <span class="dot"></span> {{ isScanPictureExitOnS3 }}
                    </span>
                  </div>

                  <p class="section-desc">
                    Kiểm tra tính toàn vẹn; nếu phát hiện ảnh thiếu trên S3, hệ thống sẽ tự động đồng bộ lại.
                  </p>

                  <div class="form-item-block mb-3">
                    <label class="item-label"><i class="el-icon-date"></i> Khoảng thời gian quét ảnh:</label>
                    <el-date-picker
                      v-model="dateRangeScanS3"
                      type="daterange"
                      align="left"
                      unlink-panels
                      range-separator="đến"
                      start-placeholder="Từ ngày"
                      end-placeholder="Đến ngày"
                      format="dd/MM/yyyy"
                      value-format="yyyy-MM-dd"
                      :picker-options="pickerOptions"
                      style="width: 100%"
                      @change="handleDateRangeScanS3Change"
                    />
                  </div>

                  <div class="section-action-footer">
                    <el-button
                      class="gradient-btn blue-btn"
                      icon="el-icon-search"
                      size="medium"
                      :disabled="isScanPictureExitOnS3 === 'Ngừng hoạt động'"
                      :loading="loadingScanPictureExitS3"
                      @click="scanPictureExitS3Amazon"
                    >
                      Tiến hành quét ảnh S3
                    </el-button>
                  </div>
                </div>
              </el-col>

              <!-- Section 1.4: Dọn ảnh thừa & ảnh xóa thủ công -->
              <el-col v-if="hasPhotoCleanupActions" :xs="24" :lg="12" class="mb-4">
                <div class="form-section">
                  <div class="section-header-row">
                    <h4 class="section-divider">
                      <i class="el-icon-brush"></i> DỌN DẸP ẢNH RÁC & ẢNH ĐÃ XÓA THỦ CÔNG
                    </h4>
                    <span class="status-badge online">
                      <span class="dot"></span> Hoạt động
                    </span>
                  </div>

                  <p class="section-desc">
                    Quét dọn các tệp ảnh rác không còn liên kết và giải phóng dung lượng từ ảnh đã xóa thủ công.
                  </p>

                  <div class="dual-action-grid" :class="{ 'single-action': isDeleteRedundantPhotoOnServer !== isDeleteS3SVHandMade }">
                    <div v-if="isDeleteRedundantPhotoOnServer === 'Hoạt động'" class="action-sub-box">
                      <div class="sub-box-title">
                        <i class="el-icon-folder-delete text-purple"></i> Dọn ảnh rác theo trường
                      </div>
                      <p class="sub-box-desc">Lựa chọn từng trường, năm & tháng áp dụng dọn ảnh.</p>
                      <el-button
                        class="gradient-btn purple-btn"
                        icon="el-icon-brush"
                        size="small"
                        :disabled="isDeleteRedundantPhotoOnServer === 'Ngừng hoạt động'"
                        @click="openDeleteRedundantPhotoDialog"
                      >
                        Chọn trường & Dọn ảnh rác
                      </el-button>
                    </div>

                    <div v-if="isDeleteS3SVHandMade === 'Hoạt động'" class="action-sub-box">
                      <div class="sub-box-title">
                        <i class="el-icon-delete text-red"></i> Dọn ảnh đã xóa thủ công
                      </div>
                      <p class="sub-box-desc">Giải phóng dung lượng từ các ảnh đã bị người dùng xóa.</p>
                      <el-button
                        class="gradient-btn red-btn"
                        icon="el-icon-delete"
                        size="small"
                        :disabled="isDeleteS3SVHandMade === 'Ngừng hoạt động'"
                        :loading="buttonLoadingS3SVHandMade"
                        @click="deleteS3SVHandMade"
                      >
                        Dọn dẹp ngay
                      </el-button>
                    </div>
                  </div>
                </div>
              </el-col>
            </el-row>
          </div>
        </el-tab-pane>

        <!-- ════════════════════════════════════════════════════════════ -->
        <!-- TAB 2: DỌN DẸP CƠ SỞ DỮ LIỆU LỚN -->
        <!-- ════════════════════════════════════════════════════════════ -->
        <el-tab-pane v-if="isDeleteDataBase === 'Hoạt động'" name="database_cleanup">
          <span slot="label">
            <i class="el-icon-coin"></i> Dọn dẹp CSDL lớn
          </span>

          <div class="tab-pane-content">
            <div class="form-section">
              <div class="section-header-row">
                <h4 class="section-divider">
                  <i class="el-icon-coin"></i> BẢO TRÌ & GIẢI PHÓNG DUNG LƯỢNG BẢNG CƠ SỞ DỮ LIỆU LỚN
                </h4>
                <span class="status-badge" :class="isDeleteDataBase === 'Hoạt động' ? 'online' : 'offline'">
                  <span class="dot"></span> {{ isDeleteDataBase }}
                </span>
              </div>

              <p class="section-desc">
                Thực hiện giải phóng dung lượng cho các bảng lịch sử (nhận xét, điểm danh) đã lưu trữ lâu năm trong cơ sở dữ liệu.
              </p>

              <!-- Select Table & Capacity Stat -->
              <el-row :gutter="20" class="mb-4">
                <el-col :xs="24" :md="10">
                  <div class="form-item-block">
                    <label class="item-label"><i class="el-icon-s-grid"></i> Chọn bảng dữ liệu cần bảo trì:</label>
                    <el-select
                      v-model="selectedOption"
                      clearable
                      placeholder="Chọn bảng dữ liệu..."
                      style="width: 100%"
                      @change="handleChangeOption"
                    >
                      <el-option
                        v-for="item in options"
                        :key="item.value"
                        :label="item.label"
                        :value="item.value"
                      />
                    </el-select>
                  </div>
                </el-col>

                <el-col :xs="24" :md="14" v-if="selectedOption !== '' && dataInfo">
                  <div class="stat-mini-container">
                    <div class="stat-mini-box">
                      <span class="stat-mini-title"><i class="el-icon-tickets"></i> Số lượng bản ghi:</span>
                      <span class="stat-mini-number">{{ formatNumber(TABLE_ROWS) }}</span>
                    </div>
                    <div class="stat-mini-divider"></div>
                    <div class="stat-mini-box">
                      <span class="stat-mini-title"><i class="el-icon-pie-chart"></i> Dung lượng ước tính:</span>
                      <span class="stat-mini-number highlight">{{ formatNumber(DATA_LENGTH) }} MB</span>
                    </div>
                  </div>
                </el-col>
              </el-row>

              <!-- Date Range Selection -->
              <el-row :gutter="20" class="mb-3">
                <el-col :xs="24" :md="14">
                  <div class="form-item-block">
                    <label class="item-label"><i class="el-icon-date"></i> Khoảng thời gian xóa dữ liệu:</label>
                    <el-date-picker
                      v-model="dateRangeDB"
                      type="daterange"
                      align="left"
                      unlink-panels
                      range-separator="đến"
                      start-placeholder="Từ ngày"
                      end-placeholder="Đến ngày"
                      format="dd/MM/yyyy"
                      value-format="yyyy-MM-dd"
                      :picker-options="pickerOptions"
                      style="width: 100%"
                      @change="handleDateRangeDBChange"
                    />
                  </div>
                </el-col>
              </el-row>

              <!-- Safety Alert -->
              <div class="notice-box warning mb-3">
                <i class="el-icon-warning"></i>
                <div>
                  <strong>Quy tắc an toàn hệ thống:</strong>
                  Chỉ cho phép xóa dữ liệu lịch sử đã lưu trữ trên <strong>1 năm (&ge; 366 ngày)</strong> so với ngày hiện tại. Thao tác xóa CSDL không thể hoàn tác!
                </div>
              </div>

              <!-- Result Message -->
              <div v-if="responseRowDeleted" class="result-alert-box mb-3">
                <i class="el-icon-info"></i>
                <span>{{ responseRowDeleted }}</span>
              </div>

              <div class="section-action-footer">
                <el-button
                  class="gradient-btn red-btn"
                  icon="el-icon-delete"
                  size="medium"
                  :disabled="isDeleteDataBase === 'Ngừng hoạt động'"
                  :loading="buttonLoading"
                  @click="deleteBigData"
                >
                  {{ nameDelete }}
                </el-button>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <!-- ════════════════════════════════════════════════════════════ -->
        <!-- TAB 3: CHUYỂN ĐỔI DỮ LIỆU V4 (MIGRATION) -->
        <!-- ════════════════════════════════════════════════════════════ -->
        <el-tab-pane v-if="hasMigrationActions" name="data_migration">
          <span slot="label">
            <i class="el-icon-refresh"></i> Chuyển đổi dữ liệu V4
          </span>

          <div class="tab-pane-content">
            <el-row :gutter="20" type="flex" class="migration-card-grid">
              <el-col v-if="hasSchoolDataMigrationAction" :xs="24" :md="12" class="mb-4">
                <div class="form-section">
                  <h4 class="section-divider">
                    <i class="el-icon-refresh"></i> CHUYỂN ĐỔI ĐIỂM DANH VÀ NHẬN XÉT V4
                  </h4>
                  <p class="section-desc">
                    Chuyển điểm danh và bốn loại nhận xét cho nhiều trường trong một lần thực hiện.
                  </p>

                  <div class="feature-checklist mb-4">
                    <div class="check-item"><i class="el-icon-check"></i> Chọn danh sách trường, khoảng ngày và dữ liệu cần chuyển</div>
                    <div class="check-item"><i class="el-icon-check"></i> Chạy ngay hoặc lưu yêu cầu chờ cronjob</div>
                    <div class="check-item"><i class="el-icon-check"></i> Theo dõi tiến độ và kết quả chuyển đổi từng trường</div>
                  </div>

                  <div class="section-action-footer school-migration-actions">
                    <el-button icon="el-icon-tickets" size="medium" @click="handleOpenSchoolMigrationJobs">
                      Yêu cầu chuyển đổi
                    </el-button>
                    <el-button class="gradient-btn blue-btn" icon="el-icon-refresh" size="medium" @click="handleOpenSchoolMigration">
                      Chuyển đổi nhiều trường
                    </el-button>
                  </div>
                </div>
              </el-col>

              <!-- Card 3.1: Migration Nhận xét -->
              <el-col v-if="hasSchoolDataMigrationAction" :xs="24" :md="12" class="mb-4">
                <div class="form-section">
                  <h4 class="section-divider">
                    <i class="el-icon-edit-outline"></i> CHUYỂN ĐỔI NHẬN XÉT HỌC SINH SANG V4
                  </h4>
                  <p class="section-desc">
                    Đồng bộ và di chuyển dữ liệu nhận xét (ngày, tuần, tháng, định kỳ) sang cấu trúc V4 mới tối ưu tốc độ.
                  </p>

                  <div class="feature-checklist mb-4">
                    <div class="check-item"><i class="el-icon-check"></i> Chuyển đổi linh hoạt theo từng trường hoặc toàn bộ</div>
                    <div class="check-item"><i class="el-icon-check"></i> Hỗ trợ lọc theo loại nhận xét và khoảng thời gian</div>
                    <div class="check-item"><i class="el-icon-check"></i> Cơ chế an toàn: Bỏ qua dữ liệu đã tồn tại, không ghi đè</div>
                  </div>

                  <div class="section-action-footer">
                    <el-button
                      class="gradient-btn blue-btn"
                      icon="el-icon-refresh"
                      size="medium"
                      @click="handleOpenMigrationEvaluation"
                    >
                      Mở công cụ chuyển đổi nhận xét V4
                    </el-button>
                  </div>
                </div>
              </el-col>

              <!-- Card 3.2: Migration Điểm danh -->
              <el-col v-if="hasSchoolDataMigrationAction" :xs="24" :md="12" class="mb-4">
                <div class="form-section">
                  <h4 class="section-divider">
                    <i class="el-icon-finished"></i> CHUYỂN ĐỔI ĐIỂM DANH HỌC SINH SANG V4
                  </h4>
                  <p class="section-desc">
                    Chuyển đổi và chuẩn hoá dữ liệu điểm danh sang phiên bản V4 đảm bảo tính chính xác và hiệu năng truy vấn.
                  </p>

                  <div class="feature-checklist mb-4">
                    <div class="check-item"><i class="el-icon-check"></i> Đồng bộ dữ liệu điểm danh đến và về</div>
                    <div class="check-item"><i class="el-icon-check"></i> Tùy chọn khoảng ngày di chuyển an toàn</div>
                    <div class="check-item"><i class="el-icon-check"></i> Báo cáo tổng hợp số lượng bản ghi thành công/thất bại</div>
                  </div>

                  <div class="section-action-footer">
                    <el-button
                      class="gradient-btn green-btn"
                      icon="el-icon-refresh"
                      size="medium"
                      @click="handleOpenMigrationAttendance"
                    >
                      Mở công cụ chuyển đổi điểm danh V4
                    </el-button>
                  </div>
                </div>
              </el-col>

              <!-- Card 3.3: Migration Album legacy -->
              <el-col v-if="hasAlbumLegacySyncAction" :xs="24" :md="12" class="mb-4">
                <div class="form-section">
                  <h4 class="section-divider">
                    <i class="el-icon-picture-outline"></i> ĐỒNG BỘ ALBUM CŨ SANG ALBUM MỚI
                  </h4>
                  <p class="section-desc">
                    Đồng bộ album trường/lớp từ dữ liệu cũ sang Album mới, giữ nguyên thông tin album và metadata storage ảnh.
                  </p>

                  <div class="feature-checklist mb-4">
                    <div class="check-item"><i class="el-icon-check"></i> Chọn một trường hoặc toàn bộ trường theo khoảng ngày</div>
                    <div class="check-item"><i class="el-icon-check"></i> Chỉ chuyển album và ảnh còn hoạt động</div>
                    <div class="check-item"><i class="el-icon-check"></i> Bỏ qua và đánh dấu album đã đồng bộ thành công</div>
                  </div>

                  <div class="section-action-footer">
                    <el-button
                      class="gradient-btn purple-btn"
                      icon="el-icon-picture-outline"
                      size="medium"
                      @click="handleOpenAlbumLegacySync"
                    >
                      Mở công cụ đồng bộ album
                    </el-button>
                  </div>
                </div>
              </el-col>
            </el-row>
          </div>
        </el-tab-pane>

        <!-- ════════════════════════════════════════════════════════════ -->
        <!-- TAB 4: TIỆN ÍCH & TÍCH HỢP HỆ THỐNG -->
        <!-- ════════════════════════════════════════════════════════════ -->
        <el-tab-pane name="system_utilities">
          <span slot="label">
            <i class="el-icon-cpu"></i> Tiện ích & Tích hợp
          </span>

          <div class="tab-pane-content">
            <el-row :gutter="20">
              <!-- Item 4.1: Khởi động lại cấu hình S3 -->
              <el-col :xs="24" :md="12" :lg="8" class="mb-4">
                <div class="form-section">
                  <h4 class="section-divider">
                    <i class="el-icon-refresh-right"></i> KHỞI ĐỘNG CẤU HÌNH S3 AMAZON
                  </h4>
                  <p class="section-desc">
                    Tải lại và làm mới các tham số cấu hình kết nối đám mây AWS S3 khi có cập nhật Secret Key, Access Key hoặc thay đổi bucket.
                  </p>

                  <div class="notice-box info mb-3">
                    <i class="el-icon-info"></i>
                    <span>Áp dụng ngay cấu hình mới mà không cần khởi động lại dịch vụ máy chủ.</span>
                  </div>

                  <div class="section-action-footer">
                    <el-button
                      class="gradient-btn blue-btn"
                      icon="el-icon-refresh"
                      size="medium"
                      :loading="loadingRestartConfiguration"
                      @click="restartConfiguration"
                    >
                      Khởi động lại S3
                    </el-button>
                  </div>
                </div>
              </el-col>

              <!-- Item 4.2: Webhook SePay -->
              <el-col :xs="24" :md="12" :lg="8" class="mb-4">
                <div class="form-section">
                  <h4 class="section-divider">
                    <i class="el-icon-lightning"></i> WEBHOOK THANH TOÁN TRỰC TUYẾN
                  </h4>
                  <p class="section-desc">
                    Kiểm tra đường truyền webhook và tình trạng tiếp nhận thông báo thanh toán tự động từ cổng kết nối SePay.
                  </p>

                  <div class="notice-box info mb-3">
                    <i class="el-icon-info"></i>
                    <span>Gửi gói tin kiểm tra kết nối tới máy chủ endpoint thanh toán SePay.</span>
                  </div>

                  <div class="section-action-footer">
                    <el-button
                      class="gradient-btn green-btn"
                      icon="el-icon-lightning"
                      size="medium"
                      :loading="loadingCheckWebHook"
                      @click="checkWebHook"
                    >
                      Kiểm tra Webhook
                    </el-button>
                  </div>
                </div>
              </el-col>

              <!-- Item 4.3: Chế độ nhận diện AI -->
              <el-col :xs="24" :md="12" :lg="8" class="mb-4">
                <div class="form-section">
                  <h4 class="section-divider">
                    <i class="el-icon-camera"></i> CHẾ ĐỘ NHẬN DIỆN KHUÔN MẶT AI
                  </h4>
                  <p class="section-desc">
                    Cập nhật và đồng bộ trạng thái kích hoạt nhận diện khuôn mặt AI theo từng trường học đến các máy điểm danh.
                  </p>

                  <div class="notice-box info mb-3">
                    <i class="el-icon-info"></i>
                    <span>Áp dụng thay đổi cấu hình điểm danh AI trên toàn bộ hệ thống máy điểm danh.</span>
                  </div>

                  <div class="section-action-footer">
                    <el-button
                      class="gradient-btn purple-btn"
                      icon="el-icon-camera"
                      size="medium"
                      :loading="loadingChangAI"
                      @click="handleChangeAI"
                    >
                      Cập nhật cấu hình AI
                    </el-button>
                  </div>
                </div>
              </el-col>
            </el-row>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>

    <!-- 4. DIALOGS -->
    <div>
      <SchoolSelectConfigS3Dialog
        :checkSchoolSelect="checkSchoolSelect"
        :isTransfer="isTransfer"
        :dateStart="dateStart"
        :dateEnd="dateEnd"
        @close="handleCloseSchoolSelect"
        @childEvent="handleChildEventTransfer"
      />
      <DeletePictureServerAndS3Dialog
        :checkSchoolSelectDelete="checkSchoolSelectDelete"
        :isDeleteAlbum="isDeleteAlbum"
        :dateStart="dateStart"
        :dateEnd="dateEnd"
        @close="handleCloseSchoolSelectDelete"
        @childEvent="handleChildEventDelete"
      />
      <DeleteRedundantPhotosDialog
        :showDeleteRedundantPhotoOnServer="showDeleteRedundantPhotoOnServer"
        @close="closeDeleteRedundantPhotoOnServerDialog"
        @childEvent="handleChildEventDeleteRedundantPhotos"
      />
      <DeleteDataTurnOffAutomaticInvoiceGeneration
        :showTurnOffAutomaticInvoiceGenerationDialog="showTurnOffAutomaticInvoiceGenerationDialog"
        :dateStart="dateStart"
        :dateEnd="dateEnd"
        @close="handleCloseTurnOffAutomaticInvoiceGeneration"
      />
      <SchoolDataMigrationPanel
        v-if="hasSchoolDataMigrationAction"
        ref="schoolDataMigration"
        :school-list="schoolList"
        :active="activeTab === 'data_migration'"
      />
      <MigrationAttendanceDialog
        v-if="hasSchoolDataMigrationAction"
        :dialog-visible="showMigrationAttendanceDialog"
        :school-list="schoolList"
        @close="handleCloseMigrationAttendance"
      />
      <MigrationEvaluationDialog
        v-if="hasSchoolDataMigrationAction"
        :dialog-visible="showMigrationEvaluationDialog"
        :school-list="schoolList"
        @close="handleCloseMigrationEvaluation"
      />
      <AlbumLegacySyncDialog
        v-if="hasAlbumLegacySyncAction"
        :dialog-visible="showAlbumLegacySyncDialog"
        :school-list="schoolList"
        @close="handleCloseAlbumLegacySync"
      />
    </div>
  </div>
</template>

<script>
import OtherService from "@/services/CommonService/OtherService";
import ManageDataService from "@/services/AdminService/ManageDataService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import SchoolSelectConfigS3Dialog from "@/views/master/other/SchoolSelectConfigS3Dialog.vue";
import DeletePictureServerAndS3Dialog from "@/views/master/other/DeletePictureServerAndS3Dialog.vue";
import DeleteDataTurnOffAutomaticInvoiceGeneration from "@/views/master/other/DeleteDataTurnOffAutomaticInvoiceGeneration.vue";
import DeleteRedundantPhotosDialog from "@/views/master/other/DeleteRedundantPhotosDialog.vue";
import sePayConnectionService from "@/services/SePayConnectionService";
import MigrationAttendanceDialog from "@/attendance_v3/components/dialog/MigrationAttendanceDialog.vue";
import MigrationEvaluationDialog from "@/views/master/other/MigrationEvaluationDialog.vue";
import SchoolDataMigrationPanel from "@/views/master/other/SchoolDataMigrationPanel.vue";
import AlbumLegacySyncDialog from "@/modules/album/AlbumLegacySyncDialog.vue";
import {appType} from "@/constant/listCommon";

export default {
  name: "ManageDataSystem",
  components: {
    SchoolDataMigrationPanel,
    MigrationAttendanceDialog,
    MigrationEvaluationDialog,
    AlbumLegacySyncDialog,
    DeleteRedundantPhotosDialog,
    DeleteDataTurnOffAutomaticInvoiceGeneration,
    DeletePictureServerAndS3Dialog,
    SchoolSelectConfigS3Dialog,
  },
  props: {
    showMessageRunAction: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: "s3_storage",
      actionStatusesReady: false,
      showCheckBoxS3: false,
      showSchoolSelectDelete: false,
      checkSchoolSelectDelete: false,
      showTurnOffAutomaticInvoiceGenerationDialog: false,
      showDeleteRedundantPhotoOnServer: false,
      loadingRestartConfiguration: false,
      checkSchoolSelect: false,
      showSchoolSelect: false,
      nameDelete: "Tiến hành xóa",
      nameButton: "Tiến hành chuyển",
      buttonLoading: false,
      buttonLoading1: false,
      schoolList: [],
      options: [
        { value: "ma_evaluate_date", label: "ma_evaluate_date (Nhận xét học sinh)" },
        { value: "ma_attendance_kids", label: "ma_attendance_kids (Điểm danh học sinh)" },
      ],
      selectedOption: "",
      dataInfo: false,
      TABLE_ROWS: 0,
      DATA_LENGTH: 0,
      DATA_INFO_LIST: [],
      responseRowDeleted: "",
      dateStart: "",
      dateEnd: "",
      checked: false,

      // Modern Date Range Bindings
      dateRangeDB: [],
      dateRangeS3: [],
      dateRangeDeleteS3: [],
      dateRangeScanS3: [],

      pickerOptions: {
        shortcuts: [
          {
            text: "Hôm nay",
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "1 tháng trước",
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setMonth(start.getMonth() - 1);
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "6 tháng trước",
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setMonth(start.getMonth() - 6);
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "1 năm trước",
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setFullYear(start.getFullYear() - 1);
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "2 năm trước",
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setFullYear(start.getFullYear() - 2);
              picker.$emit("pick", [start, end]);
            },
          },
        ],
      },

      isTransfer: "Ngừng hoạt động",
      isDeleteAlbum: "Ngừng hoạt động",
      isDeleteDataBase: "Ngừng hoạt động",
      isDeleteS3SVHandMade: "Ngừng hoạt động",
      isDeleteRedundantPhotoOnServer: "Ngừng hoạt động",
      isScanPictureExitOnS3: "Ngừng hoạt động",
      isAlbumLegacySync: "Ngừng hoạt động",
      isSchoolDataMigration: "Ngừng hoạt động",
      transferDate: "",
      buttonLoadingS3SVHandMade: false,
      loadingScanPictureExitS3: false,
      deleteRedundantPhotos: false,
      loadingCheckWebHook: false,
      loadingChangAI: false,
      showMigrationAttendanceDialog: false,
      showMigrationEvaluationDialog: false,
      showAlbumLegacySyncDialog: false,
      appType,
    };
  },
  computed: {
    getAppTypeUserLogin() {
      return this.$store.state.auth.user.appType;
    },
    hasSchoolDataMigrationAction() {
      return this.isSchoolDataMigration === "Hoạt động";
    },
    hasAlbumLegacySyncAction() {
      return this.isAlbumLegacySync === "Hoạt động" && this.getAppTypeUserLogin === this.appType.SUPPER_SYSTEM;
    },
    hasMigrationActions() {
      return this.hasSchoolDataMigrationAction || this.hasAlbumLegacySyncAction;
    },
    hasPhotoCleanupActions() {
      return this.isDeleteRedundantPhotoOnServer === "Hoạt động" || this.isDeleteS3SVHandMade === "Hoạt động";
    },
    hasS3Actions() {
      return this.isTransfer === "Hoạt động" || this.isDeleteAlbum === "Hoạt động" ||
        this.isScanPictureExitOnS3 === "Hoạt động" || this.hasPhotoCleanupActions;
    },
  },
  created() {
    const statusRequests = [
      this.getHistoryActionTransfer("SCANSERVER"),
      this.getHistoryActionDelete("DELETEPICS3SV"),
      this.getHistoryActionDeleteDataBase("DELETEDATABASE"),
      this.getHistoryActionDeleteS3SVHandMade("DELETEPICHANDMADE"),
      this.getHistoryActionDeleteRedundantPhotoOnServer("DELETEPICREDSERVER"),
      this.getHistoryActionScanPictureExitS3("SCANPICEXITS3"),
      this.getHistoryActionAlbumLegacySync("ALBUM_LEGACY_SYNC"),
      this.getHistoryActionSchoolDataMigration("SCHOOL_DATA_MIGRATION"),
    ];
    Promise.all(statusRequests.map((request) => request.catch(() => null))).then(() => {
      this.activeTab = this.hasS3Actions ? "s3_storage" :
        this.isDeleteDataBase === "Hoạt động" ? "database_cleanup" :
        this.hasMigrationActions ? "data_migration" : "system_utilities";
      this.actionStatusesReady = true;
    });
  },
  beforeMount() {
    this.getAllSchoolMethod();
  },
  methods: {
    formatNumber(num) {
      if (num === null || num === undefined) return "0";
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    handleDateRangeDBChange(val) {
      if (val && val.length === 2) {
        this.dateStart = val[0];
        this.dateEnd = val[1];
      } else {
        this.dateStart = "";
        this.dateEnd = "";
      }
    },
    handleDateRangeS3Change(val) {
      if (val && val.length === 2) {
        this.dateStart = val[0];
        this.dateEnd = val[1];
      } else {
        this.dateStart = "";
        this.dateEnd = "";
      }
    },
    handleDateRangeDeleteS3Change(val) {
      if (val && val.length === 2) {
        this.dateStart = val[0];
        this.dateEnd = val[1];
      } else {
        this.dateStart = "";
        this.dateEnd = "";
      }
    },
    handleDateRangeScanS3Change(val) {
      if (val && val.length === 2) {
        this.dateStart = val[0];
        this.dateEnd = val[1];
      } else {
        this.dateStart = "";
        this.dateEnd = "";
      }
    },

    async deleteS3SVHandMade() {
      this.$confirm(
        "Bạn có chắc chắn muốn dọn dẹp các tệp ảnh đã xóa thủ công trên hệ thống?",
        "Cảnh báo an toàn",
        {
          confirmButtonText: "Đồng ý",
          cancelButtonText: "Hủy bỏ",
          type: "warning",
        }
      ).then(() => {
        this.buttonLoadingS3SVHandMade = true;
        ManageDataService.getDeleteS3SVHandMade()
          .then(() => {
            NotifyMessage.leftCornerNotificationSuccess("Dọn dẹp ảnh xóa thủ công thành công!");
          })
          .catch((err) => {
            NotifyMessage.leftCornerNotificationError("Có lỗi xảy ra, không thể thực hiện thao tác!");
            console.error(err);
          })
          .finally(() => {
            this.buttonLoadingS3SVHandMade = false;
          });
      });
    },
    getHistoryActionAlbumLegacySync(codeAction) {
      return ManageDataService.getHistoryAction(codeAction).then((res) => {
        this.isAlbumLegacySync = res.data.data.statusAction ? "Hoạt động" : "Ngừng hoạt động";
      });
    },
    getHistoryActionSchoolDataMigration(codeAction) {
      return ManageDataService.getHistoryAction(codeAction).then((res) => {
        this.isSchoolDataMigration = res.data.data.statusAction ? "Hoạt động" : "Ngừng hoạt động";
      });
    },
    getHistoryActionDeleteS3SVHandMade(codeAction) {
      return ManageDataService.getHistoryAction(codeAction).then((res) => {
        if (res.data.data.statusAction) {
          this.isDeleteS3SVHandMade = "Hoạt động";
        } else {
          this.isDeleteS3SVHandMade = "Ngừng hoạt động";
        }
      });
    },
    getHistoryActionDeleteDataBase(codeAction) {
      return ManageDataService.getHistoryAction(codeAction).then((res) => {
        if (res.data.data.statusAction) {
          this.isDeleteDataBase = "Hoạt động";
        } else {
          this.isDeleteDataBase = "Ngừng hoạt động";
        }
      });
    },
    getHistoryActionDeleteRedundantPhotoOnServer(codeAction) {
      return ManageDataService.getHistoryAction(codeAction).then((res) => {
        if (res.data.data.statusAction) {
          this.isDeleteRedundantPhotoOnServer = "Hoạt động";
        } else {
          this.isDeleteRedundantPhotoOnServer = "Ngừng hoạt động";
        }
      });
    },
    getHistoryActionScanPictureExitS3(codeAction) {
      return ManageDataService.getHistoryAction(codeAction).then((res) => {
        if (res.data.data.statusAction) {
          this.isScanPictureExitOnS3 = "Hoạt động";
        } else {
          this.isScanPictureExitOnS3 = "Ngừng hoạt động";
        }
      });
    },
    handleChildEventDelete(data) {
      if (data === true) {
        this.getMessageAction("DELETEPICS3SV");
      }
    },
    handleChildEventDeleteRedundantPhotos(data) {
      if (data === true) {
        this.getMessageAction("DELETEPICREDSERVER");
      }
    },
    handleChildEventTransfer(data) {
      this.showSchoolSelect = false;
      this.checkSchoolSelect = false;
      if (data === true) {
        this.getMessageAction("SCANSERVER");
      }
    },
    getHistoryActionTransfer(codeAction) {
      return ManageDataService.getHistoryAction(codeAction).then((res) => {
        this.transferDate = res.data.data.transferDate;
        if (res.data.data.statusAction) {
          this.isTransfer = "Hoạt động";
        } else {
          this.isTransfer = "Ngừng hoạt động";
        }
      });
    },
    getHistoryActionDelete(codeAction) {
      return ManageDataService.getHistoryAction(codeAction).then((res) => {
        if (res.data.data.statusAction) {
          this.isDeleteAlbum = "Hoạt động";
        } else {
          this.isDeleteAlbum = "Ngừng hoạt động";
        }
      });
    },
    handleCloseSchoolSelect() {
      this.checkSchoolSelect = false;
      this.showSchoolSelect = false;
    },
    handleCloseSchoolSelectDelete() {
      this.checkSchoolSelectDelete = false;
      this.showSchoolSelectDelete = false;
    },
    getAllSchoolMethod() {
      OtherService.getSchoolAllService()
        .then((resp) => {
          this.schoolList = resp.data.data;
        })
        .catch((err) => {
          this.$message({
            message: err.response.data.message,
            type: "error",
          });
        });
    },
    isValidDate(dateString) {
      const regex = /^\d{4}-\d{2}-\d{2}$/;
      return regex.test(dateString);
    },
    handleDate() {
      if (!this.isValidDate(this.dateStart) || !this.isValidDate(this.dateEnd)) {
        NotifyMessage.leftCornerNotificationError("Vui lòng chọn khoảng thời gian hợp lệ!");
        return false;
      }
      const firstDate = new Date(this.dateStart);
      const secondDate = new Date(this.dateEnd);
      const currentDate = new Date();
      if (secondDate < firstDate) {
        NotifyMessage.leftCornerNotificationError("Ngày kết thúc không được nhỏ hơn ngày bắt đầu!");
        return false;
      }
      if (currentDate < secondDate) {
        NotifyMessage.leftCornerNotificationError("Ngày kết thúc không được lớn hơn ngày hiện tại!");
        return false;
      }
      const differenceInTime = Math.abs(currentDate.getTime() - secondDate.getTime());
      const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));

      if (differenceInDays < 366) {
        NotifyMessage.leftCornerNotificationError("Hệ thống chỉ cho phép xóa dữ liệu đã lưu trữ trên 1 năm!");
        return false;
      }
      return true;
    },
    async handleChangeOption() {
      if (!this.selectedOption) {
        this.dataInfo = false;
        return;
      }
      try {
        const resp = await ManageDataService.findCapacityFromDB(this.selectedOption);
        this.DATA_INFO_LIST = resp.data.data[0];
        this.TABLE_ROWS = this.DATA_INFO_LIST[1];
        const number1 = (this.DATA_INFO_LIST[2] + this.DATA_INFO_LIST[3]) / (1024 * 1024);
        this.DATA_LENGTH = Math.round(number1);
        this.dataInfo = true;
      } catch (err) {
        console.error(err);
      }
    },
    async deleteBigData() {
      if (!this.selectedOption) {
        NotifyMessage.leftCornerNotificationError("Hãy chọn bảng cơ sở dữ liệu cần xóa!");
        return;
      }
      if (!this.handleDate()) {
        return;
      }
      this.$confirm(
        `Bạn có chắc chắn muốn xóa dữ liệu bảng "${this.selectedOption}" từ ngày ${this.dateStart} đến ngày ${this.dateEnd}? Thao tác này KHÔNG thể hoàn tác!`,
        "Cảnh báo xóa dữ liệu lớn",
        {
          confirmButtonText: "Xác nhận xóa",
          cancelButtonText: "Hủy bỏ",
          type: "warning",
        }
      ).then(async () => {
        this.buttonLoading = true;
        this.responseRowDeleted = "Hệ thống đang tiến hành xử lý xóa dữ liệu...";
        this.nameDelete = "Đang tiến hành xóa, xin vui lòng đợi";
        try {
          const resp = await ManageDataService.deleteCapacityFromDB(
            this.dateStart,
            this.dateEnd,
            this.selectedOption
          );
          this.responseRowDeleted = resp.data.message;
          NotifyMessage.leftCornerNotificationSuccess("Đã xóa dữ liệu thành công!");
          this.handleChangeOption();
        } catch (err) {
          this.responseRowDeleted = "Có lỗi xảy ra, không thể thực hiện thao tác xóa!";
          NotifyMessage.leftCornerNotificationError(this.responseRowDeleted);
          console.error(err);
        } finally {
          this.buttonLoading = false;
          this.checked = false;
          this.nameDelete = "Tiến hành xóa";
        }
      });
    },
    transferDataServerToS3() {
      if (!this.dateRangeS3 || this.dateRangeS3.length < 2) {
        NotifyMessage.leftCornerNotificationError("Vui lòng chọn khoảng thời gian bắt đầu và kết thúc!");
        return;
      }
      this.dateStart = this.dateRangeS3[0];
      this.dateEnd = this.dateRangeS3[1];
      if (!this.isValidDate(this.dateStart) || !this.isValidDate(this.dateEnd)) {
        NotifyMessage.leftCornerNotificationError("Định dạng ngày không hợp lệ!");
        return;
      }
      const firstDate = new Date(this.dateStart);
      const secondDate = new Date(this.dateEnd);
      const currentDate = new Date();
      if (secondDate < firstDate) {
        NotifyMessage.leftCornerNotificationError("Ngày kết thúc không được nhỏ hơn ngày bắt đầu!");
        return;
      }
      if (currentDate < secondDate) {
        NotifyMessage.leftCornerNotificationError("Ngày kết thúc không được lớn hơn ngày hiện tại!");
        return;
      }
      this.checkSchoolSelect = true;
    },
    deleteDateServerAndS3() {
      if (!this.dateRangeDeleteS3 || this.dateRangeDeleteS3.length < 2) {
        NotifyMessage.leftCornerNotificationError("Vui lòng chọn khoảng thời gian bắt đầu và kết thúc!");
        return;
      }
      this.dateStart = this.dateRangeDeleteS3[0];
      this.dateEnd = this.dateRangeDeleteS3[1];
      if (!this.isValidDate(this.dateStart) || !this.isValidDate(this.dateEnd)) {
        NotifyMessage.leftCornerNotificationError("Định dạng ngày không hợp lệ!");
        return;
      }
      const firstDate = new Date(this.dateStart);
      const secondDate = new Date(this.dateEnd);
      const currentDate = new Date();
      if (secondDate < firstDate) {
        NotifyMessage.leftCornerNotificationError("Ngày kết thúc không được nhỏ hơn ngày bắt đầu!");
        return;
      }
      if (currentDate < secondDate) {
        NotifyMessage.leftCornerNotificationError("Ngày kết thúc không được lớn hơn ngày hiện tại!");
        return;
      }
      this.checkSchoolSelectDelete = true;
    },
    openDeleteRedundantPhotoDialog() {
      this.showDeleteRedundantPhotoOnServer = true;
    },
    getMessageAction(codeAction) {
      let successNotified = false;
      this.showCheckBoxS3 = true;
      this.$notify.info({
        title: "Thông báo tiến trình",
        message: "Dữ liệu đang được hệ thống xử lý ngầm!",
      });
      const checkProcess = setInterval(() => {
        if (successNotified) return;

        ManageDataService.getMessageAction(codeAction)
          .then((res) => {
            if (res.data.data.message === "processing") {
              return;
            }
            if (res.data.data.message === "fail") {
              clearInterval(checkProcess);
              this.$notify.error({
                title: "Thông báo lỗi",
                message: "Quá trình xử lý dữ liệu gặp lỗi!",
              });
              this.showCheckBoxS3 = false;
              return;
            }
            if (res.data.data.message === "complete" && !successNotified) {
              successNotified = true;
              this.$notify.success({
                title: "Thông báo hoàn thành",
                message: "Dữ liệu đã được xử lý hoàn tất!",
                duration: 4000,
                onClose: () => {
                  clearInterval(checkProcess);
                },
              });
              this.showCheckBoxS3 = false;
            }
          })
          .catch((err) => {
            clearInterval(checkProcess);
            this.showCheckBoxS3 = false;
            return Promise.reject(err);
          });
      }, 10000);
    },
    handleCloseTurnOffAutomaticInvoiceGeneration() {
      this.showTurnOffAutomaticInvoiceGenerationDialog = false;
      this.showSchoolSelect = false;
    },
    closeDeleteRedundantPhotoOnServerDialog() {
      this.showDeleteRedundantPhotoOnServer = false;
      this.deleteRedundantPhotos = false;
    },
    async restartConfiguration() {
      this.loadingRestartConfiguration = true;
      try {
        const response = await ManageDataService.restartConfiguration();
        if (response.data.data === true) {
          NotifyMessage.leftCornerNotificationSuccess("Khởi động lại cấu hình S3 thành công!");
        }
      } catch (err) {
        NotifyMessage.leftCornerNotificationError("Khởi động lại cấu hình S3 thất bại!");
        console.error(err);
      } finally {
        this.loadingRestartConfiguration = false;
      }
    },
    async checkWebHook() {
      this.loadingCheckWebHook = true;
      try {
        const response = await sePayConnectionService.checkWebhookConnection();
        if (response.data.status === 200) {
          NotifyMessage.leftCornerNotificationSuccess(response.data.message || "Webhook SePay đang hoạt động bình thường!");
        }
      } catch (err) {
        NotifyMessage.leftCornerNotificationError("Chưa hoạt động, vui lòng kiểm tra hệ thống SePay!");
        console.error(err);
      } finally {
        this.loadingCheckWebHook = false;
      }
    },
    async handleChangeAI() {
      this.loadingChangAI = true;
      try {
        await ManageDataService.checkAndRunEnableDisableAttendanceOnDevice();
        NotifyMessage.leftCornerNotificationSuccess("Cập nhật chế độ nhận diện AI thành công!");
      } catch (err) {
        NotifyMessage.leftCornerNotificationError("Cập nhật chế độ nhận diện AI thất bại!");
        console.error(err);
      } finally {
        this.loadingChangAI = false;
      }
    },
    async scanPictureExitS3Amazon() {
      if (!this.dateRangeScanS3 || this.dateRangeScanS3.length < 2) {
        NotifyMessage.leftCornerNotificationError("Vui lòng chọn khoảng thời gian quét ảnh!");
        return;
      }
      this.dateStart = this.dateRangeScanS3[0];
      this.dateEnd = this.dateRangeScanS3[1];
      if (!this.isValidDate(this.dateStart) || !this.isValidDate(this.dateEnd)) {
        NotifyMessage.leftCornerNotificationError("Định dạng ngày không hợp lệ!");
        return;
      }
      const firstDate = new Date(this.dateStart);
      const secondDate = new Date(this.dateEnd);
      const currentDate = new Date();
      if (secondDate < firstDate) {
        NotifyMessage.leftCornerNotificationError("Ngày kết thúc không được nhỏ hơn ngày bắt đầu!");
        return;
      }
      if (currentDate < secondDate) {
        NotifyMessage.leftCornerNotificationError("Ngày kết thúc không được lớn hơn ngày hiện tại!");
        return;
      }
      this.loadingScanPictureExitS3 = true;
      try {
        await ManageDataService.scanPictureExitS3(this.dateStart, this.dateEnd);
        NotifyMessage.leftCornerNotificationSuccess("Đã bắt đầu tiến trình quét ảnh trên S3!");
      } catch (err) {
        NotifyMessage.leftCornerNotificationError("Có lỗi xảy ra, không thể thực hiện thao tác!");
        console.error(err);
      } finally {
        this.loadingScanPictureExitS3 = false;
      }
      this.getMessageAction("SCANPICEXITS3");
    },
    handleOpenSchoolMigration() {
      if (!this.hasSchoolDataMigrationAction) return;
      this.$refs.schoolDataMigration.openCreateDialog();
    },
    handleOpenSchoolMigrationJobs() {
      if (!this.hasSchoolDataMigrationAction) return;
      this.$refs.schoolDataMigration.openJobsDialog();
    },
    handleOpenMigrationAttendance() {
      if (!this.hasSchoolDataMigrationAction) return;
      this.showMigrationAttendanceDialog = true;
    },
    handleCloseMigrationAttendance() {
      this.showMigrationAttendanceDialog = false;
    },
    handleOpenMigrationEvaluation() {
      if (!this.hasSchoolDataMigrationAction) return;
      this.showMigrationEvaluationDialog = true;
    },
    handleCloseMigrationEvaluation() {
      this.showMigrationEvaluationDialog = false;
    },
    handleOpenAlbumLegacySync() {
      if (!this.hasAlbumLegacySyncAction) return;
      this.showAlbumLegacySyncDialog = true;
    },
    handleCloseAlbumLegacySync() {
      this.showAlbumLegacySyncDialog = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.ai-check-container {
  padding: 16px 20px 40px;
  background-color: #f8fafc;
  min-height: calc(100vh - 60px);
}

/* 1. HEADER BANNER */
.ai-header-banner {
  position: relative;
  border-radius: 14px;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  color: #ffffff;
  padding: 22px 28px;
  margin-bottom: 20px;
  box-shadow: 0 10px 25px -5px rgba(30, 60, 114, 0.3);
  overflow: hidden;

  .banner-overlay {
    position: absolute;
    top: -40%;
    right: -10%;
    width: 380px;
    height: 380px;
    background: radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.15) 0%, transparent 60%);
    pointer-events: none;
  }

  .banner-content {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
  }

  .banner-left {
    display: flex;
    align-items: center;
    gap: 18px;

    .ai-icon-pulse {
      width: 58px;
      height: 58px;
      background: rgba(255, 255, 255, 0.15);
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      border: 1px solid rgba(255, 255, 255, 0.3);
      animation: iconPulse 3s infinite ease-in-out;
      font-size: 28px;
      color: #ffffff;
      flex-shrink: 0;
    }

    .banner-title {
      font-size: 20px;
      font-weight: 800;
      letter-spacing: 0.5px;
      margin: 0 0 4px 0;
      color: #ffffff;
    }

    .banner-subtitle {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.85);
      margin: 0;
      max-width: 650px;
      line-height: 1.5;
    }
  }
}

@keyframes iconPulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.2);
  }
  50% {
    transform: scale(1.06);
    box-shadow: 0 0 25px rgba(255, 255, 255, 0.4);
  }
}

/* 2. GLASS CONTAINER & STANDARD SK-TABS */
.glass-card {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
  padding: 18px 20px;
}

/* Style tab chuẩn OneKids theo docs/guidelines.md Section 5 */
/deep/ .sk-tabs {
  .el-tabs__header {
    margin-bottom: 20px;
    border-bottom: 2px solid #2563eb;
  }

  .el-tabs__nav {
    background: transparent;
    border: none;
  }

  .el-tabs__nav-wrap::after {
    background: #2563eb;
    height: 2px;
  }

  .el-tabs__item {
    background: #e5e7eb;
    color: #4b5563;
    font-weight: 600;
    font-size: 13.5px;
    border: 1px solid #d1d5db !important;
    border-bottom: none !important;
    border-radius: 8px 8px 0 0;
    height: 42px;
    line-height: 42px;
    padding: 0 20px;
    margin-right: 4px;
    transition: background 0.15s, color 0.15s;

    i {
      margin-right: 6px;
    }

    &:hover:not(.is-active) {
      background: #d1d5db;
      color: #1e293b;
    }

    &.is-active {
      color: #ffffff !important;
      background: #2563eb !important;
      border-color: #2563eb !important;
    }
  }

  &.el-tabs--card > .el-tabs__header .el-tabs__item.is-active {
    border-bottom-color: #2563eb;
  }
}

.tab-pane-content {
  padding: 4px 0 10px;
}

/* 4. FORM SECTIONS (Clean cards inside tabs) */
.form-section {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 18px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
  height: calc(100% - 18px);

  .section-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f1f5f9;
  }

  .section-divider {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: #2563eb;
    display: flex;
    align-items: center;
    gap: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    i {
      font-size: 16px;
    }
  }

  .section-desc {
    font-size: 13px;
    color: #64748b;
    margin: 0 0 14px 0;
    line-height: 1.5;
  }

  .section-action-footer {
    margin-top: auto;
    padding-top: 14px;
    display: flex;
    justify-content: flex-end;
  }
}

.migration-card-grid {
  flex-wrap: wrap;
  align-items: stretch;

  > .el-col {
    display: flex;
    min-width: 0;
  }

  .form-section {
    width: 100%;
    height: auto;
    box-sizing: border-box;
  }

  .section-action-footer {
    flex-wrap: wrap;
    gap: 10px;

    .el-button {
      max-width: 100%;
      white-space: normal;
      line-height: 1.4;
    }

    .el-button + .el-button {
      margin-left: 0;
    }
  }
}

.form-item-block {
  .item-label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 8px;

    i {
      color: #64748b;
      margin-right: 4px;
    }
  }
}

/* 5. DUAL ACTION GRID FOR CARD 1.4 */
.dual-action-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 8px;

  &.single-action {
    grid-template-columns: 1fr;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }

  .action-sub-box {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 10px;

    .sub-box-title {
      font-size: 13px;
      font-weight: 700;
      color: #1e293b;
      display: flex;
      align-items: center;
      gap: 6px;

      i {
        font-size: 16px;
      }
    }

    .sub-box-desc {
      font-size: 12px;
      color: #64748b;
      margin: 0;
      line-height: 1.4;
      flex: 1;
    }
  }
}

/* 6. STATUS BADGES */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 9999px;

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  &.online {
    background-color: #f0fdf4;
    color: #166534;
    .dot {
      background-color: #22c55e;
      box-shadow: 0 0 8px #22c55e;
      animation: pulseGreen 2s infinite;
    }
  }

  &.offline {
    background-color: #fef2f2;
    color: #991b1b;
    .dot {
      background-color: #ef4444;
    }
  }
}

@keyframes pulseGreen {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(34, 197, 94, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
  }
}

/* 7. GRADIENT BUTTONS */
.gradient-btn {
  border: none !important;
  color: #ffffff !important;
  font-weight: 600 !important;
  border-radius: 6px !important;
  transition: all 0.3s ease !important;

  &.green-btn {
    background: linear-gradient(90deg, #10b981 0%, #059669 100%) !important;
    box-shadow: 0 4px 10px rgba(16, 185, 129, 0.3);
    &:hover {
      opacity: 0.9;
      transform: translateY(-1px);
    }
  }

  &.blue-btn {
    background: linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%) !important;
    box-shadow: 0 4px 10px rgba(59, 130, 246, 0.3);
    &:hover {
      opacity: 0.9;
      transform: translateY(-1px);
    }
  }

  &.purple-btn {
    background: linear-gradient(90deg, #8b5cf6 0%, #6d28d9 100%) !important;
    box-shadow: 0 4px 10px rgba(139, 92, 246, 0.3);
    &:hover {
      opacity: 0.9;
      transform: translateY(-1px);
    }
  }

  &.red-btn {
    background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%) !important;
    box-shadow: 0 4px 10px rgba(239, 68, 68, 0.3);
    &:hover {
      opacity: 0.9;
      transform: translateY(-1px);
    }
  }
}

/* 8. NOTICE BOXES */
.notice-box {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.5;

  i {
    font-size: 16px;
    margin-top: 2px;
    flex-shrink: 0;
  }

  &.info {
    background-color: #eff6ff;
    border: 1px solid #bfdbfe;
    color: #1d4ed8;
  }

  &.warning {
    background-color: #fffbeb;
    border: 1px solid #fde68a;
    color: #b45309;
  }
}

.result-alert-box {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 8px;
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
  font-size: 13px;
  font-weight: 600;

  i {
    font-size: 18px;
  }
}

/* 9. STAT MINI CONTAINER */
.stat-mini-container {
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 16px;
  gap: 20px;
  height: calc(100% - 10px);

  .stat-mini-box {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .stat-mini-title {
      font-size: 12px;
      color: #64748b;
      font-weight: 600;

      i {
        margin-right: 4px;
      }
    }

    .stat-mini-number {
      font-size: 16px;
      font-weight: 800;
      color: #1e293b;

      &.highlight {
        color: #2563eb;
      }
    }
  }

  .stat-mini-divider {
    width: 1px;
    height: 36px;
    background-color: #cbd5e1;
  }
}

/* 10. CHECKLIST */
.feature-checklist {
  display: flex;
  flex-direction: column;
  gap: 10px;

  .check-item {
    font-size: 13px;
    color: #334155;
    display: flex;
    align-items: center;
    gap: 8px;

    i {
      color: #16a34a;
      font-weight: bold;
    }
  }
}

.text-purple {
  color: #8b5cf6;
}

.text-red {
  color: #ef4444;
}

.mb-3 {
  margin-bottom: 12px;
}

.mb-4 {
  margin-bottom: 18px;
}
</style>
